import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Download, Printer } from "lucide-react";
import { Separator } from "@/components/ui/separator";

interface AgendaItem {
  id?: string;
  sort_order: number;
  item_type: string;
  title: string;
  discussion_notes: string;
  resolution: string;
  task_description: string;
  responsible_person: string;
  deadline: string;
  resources_required: string;
}

interface MeetingMinutesDialogProps {
  open: boolean;
  onClose: () => void;
  meetingId: string;
  meetingTitle: string;
  isCreator: boolean;
}

export default function MeetingMinutesDialog({ open, onClose, meetingId, meetingTitle, isCreator }: MeetingMinutesDialogProps) {
  const [agendaItems, setAgendaItems] = useState<AgendaItem[]>([]);
  const [minutesContent, setMinutesContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [participants, setParticipants] = useState<any[]>([]);
  const [meeting, setMeeting] = useState<any>(null);

  useEffect(() => {
    if (!open || !meetingId) return;
    loadData();
  }, [open, meetingId]);

  const loadData = async () => {
    setLoading(true);
    const [agendaRes, minutesRes, participantsRes, meetingRes] = await Promise.all([
      supabase.from("meeting_agenda_items").select("*").eq("meeting_id", meetingId).order("sort_order"),
      supabase.from("meeting_minutes").select("*").eq("meeting_id", meetingId).maybeSingle(),
      supabase.from("meeting_participants").select("*").eq("meeting_id", meetingId),
      supabase.from("meetings").select("*").eq("id", meetingId).single(),
    ]);

    if (agendaRes.data) setAgendaItems(agendaRes.data.map(a => ({
      id: a.id,
      sort_order: a.sort_order,
      item_type: a.item_type,
      title: a.title,
      discussion_notes: a.discussion_notes || "",
      resolution: a.resolution || "",
      task_description: a.task_description || "",
      responsible_person: a.responsible_person || "",
      deadline: a.deadline || "",
      resources_required: a.resources_required || "",
    })));
    if (minutesRes.data) setMinutesContent(minutesRes.data.content || "");
    if (participantsRes.data) setParticipants(participantsRes.data);
    if (meetingRes.data) setMeeting(meetingRes.data);
    setLoading(false);
  };

  const addAgendaItem = () => {
    setAgendaItems(prev => [...prev, {
      sort_order: prev.length,
      item_type: "agenda",
      title: "",
      discussion_notes: "",
      resolution: "",
      task_description: "",
      responsible_person: "",
      deadline: "",
      resources_required: "",
    }]);
  };

  const updateAgendaItem = (index: number, field: keyof AgendaItem, value: string) => {
    setAgendaItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const removeAgendaItem = (index: number) => {
    setAgendaItems(prev => prev.filter((_, i) => i !== index));
  };

  const saveAll = async () => {
    setLoading(true);
    try {
      // Delete existing agenda items and re-insert
      await supabase.from("meeting_agenda_items").delete().eq("meeting_id", meetingId);
      if (agendaItems.length > 0) {
        const { error } = await supabase.from("meeting_agenda_items").insert(
          agendaItems.map((item, i) => ({
            meeting_id: meetingId,
            sort_order: i,
            item_type: item.item_type,
            title: item.title,
            discussion_notes: item.discussion_notes || null,
            resolution: item.resolution || null,
            task_description: item.task_description || null,
            responsible_person: item.responsible_person || null,
            deadline: item.deadline || null,
            resources_required: item.resources_required || null,
          }))
        );
        if (error) throw error;
      }

      // Upsert minutes
      const { error: minErr } = await supabase.from("meeting_minutes").upsert({
        meeting_id: meetingId,
        content: minutesContent,
      }, { onConflict: "meeting_id" });
      if (minErr) throw minErr;

      toast.success("Meeting minutes saved");
    } catch (err: any) {
      toast.error(err.message || "Failed to save");
    }
    setLoading(false);
  };

  const generateMinutes = () => {
    const lines: string[] = [];
    lines.push(`MEETING MINUTES`);
    lines.push(`===============`);
    lines.push(`Title: ${meetingTitle}`);
    if (meeting?.department) lines.push(`Department: ${meeting.department}`);
    if (meeting?.started_at) lines.push(`Date: ${new Date(meeting.started_at).toLocaleDateString()}`);
    if (meeting?.started_at) lines.push(`Start Time: ${new Date(meeting.started_at).toLocaleTimeString()}`);
    if (meeting?.ended_at) lines.push(`End Time: ${new Date(meeting.ended_at).toLocaleTimeString()}`);
    lines.push("");
    lines.push(`PARTICIPANTS:`);
    participants.forEach(p => {
      lines.push(`  - ${p.display_name || "Unknown"} (${p.role || "Member"})`);
    });
    lines.push("");

    agendaItems.forEach((item, i) => {
      lines.push(`${i + 1}. ${item.title}`);
      if (item.discussion_notes) lines.push(`   Discussion: ${item.discussion_notes}`);
      if (item.resolution) lines.push(`   Resolution: ${item.resolution}`);
      if (item.task_description) lines.push(`   Task: ${item.task_description}`);
      if (item.responsible_person) lines.push(`   Responsible: ${item.responsible_person}`);
      if (item.deadline) lines.push(`   Deadline: ${item.deadline}`);
      if (item.resources_required) lines.push(`   Resources: ${item.resources_required}`);
      lines.push("");
    });

    lines.push("SIGNATURES:");
    participants.forEach(p => {
      lines.push(`  ${p.display_name || "Unknown"}: ___________________`);
    });

    setMinutesContent(lines.join("\n"));
  };

  const exportMinutes = (format: "txt") => {
    const blob = new Blob([minutesContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `minutes-${meetingTitle.replace(/\s+/g, "-")}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printMinutes = () => {
    const win = window.open("", "_blank");
    if (win) {
      const escapeHtml = (s: string) =>
        s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
      const safeTitle = escapeHtml(meetingTitle);
      const safeContent = escapeHtml(minutesContent);
      win.document.write(`<html><head><title>Meeting Minutes - ${safeTitle}</title><style>body{font-family:serif;white-space:pre-wrap;padding:2rem;line-height:1.6}</style></head><body><pre style="font-family:serif;white-space:pre-wrap">${safeContent}</pre></body></html>`);
      win.document.close();
      win.print();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" /> Meeting Minutes: {meetingTitle}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : (
          <div className="space-y-6">
            {/* Agenda Items */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm">Agenda Items</h3>
                {isCreator && (
                  <Button size="sm" variant="outline" onClick={addAgendaItem}>
                    <Plus className="h-3 w-3 mr-1" /> Add Item
                  </Button>
                )}
              </div>

              {agendaItems.map((item, index) => (
                <div key={index} className="border rounded-lg p-3 mb-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-muted-foreground">#{index + 1}</span>
                    <Input
                      placeholder="Agenda item title"
                      value={item.title}
                      onChange={(e) => updateAgendaItem(index, "title", e.target.value)}
                      disabled={!isCreator}
                      className="flex-1"
                    />
                    {isCreator && (
                      <Button size="icon" variant="ghost" onClick={() => removeAgendaItem(index)}>
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Discussion Notes</Label>
                      <Textarea rows={2} value={item.discussion_notes} onChange={(e) => updateAgendaItem(index, "discussion_notes", e.target.value)} disabled={!isCreator} />
                    </div>
                    <div>
                      <Label className="text-xs">Resolution</Label>
                      <Textarea rows={2} value={item.resolution} onChange={(e) => updateAgendaItem(index, "resolution", e.target.value)} disabled={!isCreator} />
                    </div>
                    <div>
                      <Label className="text-xs">Task</Label>
                      <Input value={item.task_description} onChange={(e) => updateAgendaItem(index, "task_description", e.target.value)} disabled={!isCreator} />
                    </div>
                    <div>
                      <Label className="text-xs">Responsible Person</Label>
                      <Input value={item.responsible_person} onChange={(e) => updateAgendaItem(index, "responsible_person", e.target.value)} disabled={!isCreator} />
                    </div>
                    <div>
                      <Label className="text-xs">Deadline</Label>
                      <Input type="date" value={item.deadline} onChange={(e) => updateAgendaItem(index, "deadline", e.target.value)} disabled={!isCreator} />
                    </div>
                    <div>
                      <Label className="text-xs">Resources Required</Label>
                      <Input value={item.resources_required} onChange={(e) => updateAgendaItem(index, "resources_required", e.target.value)} disabled={!isCreator} />
                    </div>
                  </div>
                </div>
              ))}

              {agendaItems.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No agenda items yet</p>
              )}
            </div>

            <Separator />

            {/* Meeting Minutes */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm">Final Minutes</h3>
                <div className="flex gap-2">
                  {isCreator && (
                    <Button size="sm" variant="outline" onClick={generateMinutes}>
                      <FileText className="h-3 w-3 mr-1" /> Auto-Generate
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => exportMinutes("txt")}>
                    <Download className="h-3 w-3 mr-1" /> Export
                  </Button>
                  <Button size="sm" variant="outline" onClick={printMinutes}>
                    <Printer className="h-3 w-3 mr-1" /> Print
                  </Button>
                </div>
              </div>
              <Textarea
                rows={15}
                value={minutesContent}
                onChange={(e) => setMinutesContent(e.target.value)}
                disabled={!isCreator}
                placeholder="Meeting minutes will appear here..."
                className="font-mono text-xs"
              />
            </div>

            {/* Participants list */}
            <div>
              <h3 className="font-semibold text-sm mb-2">Participants ({participants.length})</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {participants.map(p => (
                  <div key={p.id} className="text-xs border rounded px-2 py-1">
                    <div className="font-medium">{p.display_name || "Unknown"}</div>
                    <div className="text-muted-foreground">{p.role || "Member"}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {isCreator && (
            <Button onClick={saveAll} disabled={loading}>
              Save Minutes
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
